// 2026-06-20 (user request) — Kas & Bank: fitur baru di grup Keuangan berisi
// 2 tab top-level: Weekly Petty Cash · Buku Bank. Deep-link: /kas-bank?tab=petty-cash|buku-bank.
// Route lama /petty-cash di-redirect ke sini (query param ?open=/?comment= ikut terbawa).
// 2026-02 (Kas & Bank Classification Phase 1): 3 view klasifikasi + sub-tab
// "Belum Diklasifikasi" (dengan badge counter) di dalam Buku Bank.
// 2026-09-23 (user request): Penerimaan / Pembayaran / Transfer Bank dipindah
// MENJADI sub-tab di dalam Buku Bank (bukan tab top-level). Link lama
// ?tab=penerimaan|pembayaran|transfer tetap didukung (auto-dialihkan ke
// tab=buku-bank&sub=terkait).
import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Wallet, Landmark, History, ArrowDownCircle, ArrowUpCircle, Repeat } from "lucide-react";
import PettyCashPage from "@/pages/PettyCashPage";
import BankHistoryTab from "@/components/kas-bank/BankHistoryTab";
import BankRegisterTab from "@/components/kas-bank/BankRegisterTab";
import ClassificationListTab from "@/components/kas-bank/ClassificationListTab";
import { fetchSummary } from "@/lib/kasBankClassification";
import { useAccess } from "@/lib/access";
import { useAuth } from "@/lib/auth";

const TABS = [
  { key: "petty-cash", label: "Weekly Petty Cash", icon: Wallet },
  { key: "buku-bank", label: "Buku Bank", icon: Landmark },
];

// Sub-pilihan di dalam tab Buku Bank: 2026-09-23 (user request) — Penerimaan/
// Pembayaran/Transfer Bank dipindah ke DALAM Buku Bank (bukan tab top-level).
// 2026-09-23 (user request): sub-tab "register" diganti nama menjadi
// "Input & Categorize" dan digeser ke sebelah kanan tab Transfer Bank.
// 2026-09-23 (user request): sub-tab "Belum Diklasifikasi" DIHAPUS dari bar
// ini — kontennya digabung ke tab Breakdown (di dalam Input & Categorize).
const BUKU_SUBTABS = [
  { key: "history-bank", label: "Riwayat Bank", icon: History },
  { key: "penerimaan", label: "Penerimaan", icon: ArrowDownCircle },
  { key: "pembayaran", label: "Pembayaran", icon: ArrowUpCircle },
  { key: "transfer", label: "Transfer Bank", icon: Repeat },
  { key: "register", label: "Input & Categorize", icon: Landmark },
];

export default function KasBankPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("tab");
  const subQ = searchParams.get("sub");
  // Backward-compat (2026-09-23): link lama ?tab=history-bank / ?tab=penerimaan /
  // ?tab=pembayaran / ?tab=transfer dialihkan ke Buku Bank → sub-tab terkait.
  const LEGACY_SUB = new Set(["history-bank", "penerimaan", "pembayaran", "transfer", "unclassified"]);
  const legacySub = q && LEGACY_SUB.has(q) && !TABS.some((t) => t.key === q) ? q : null;
  const tab = legacySub ? "buku-bank" : (TABS.some((t) => t.key === q) ? q : "petty-cash");
  const setTab = (k) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    p.set("tab", k);
    if (k !== "buku-bank") p.delete("sub");
    return p;
  }, { replace: true });
  // Link lama ?tab=unclassified / ?sub=unclassified → dialihkan ke Input &
  // Categorize (Breakdown sekarang memuat daftar belum diklasifikasi).
  const sub = (legacySub === "unclassified" || subQ === "unclassified") ? "register"
    : legacySub || (BUKU_SUBTABS.some((s) => s.key === subQ) ? subQ : "history-bank");
  const setSub = (k) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    if (k === "history-bank") p.delete("sub"); else p.set("sub", k);
    return p;
  }, { replace: true });

  const [summary, setSummary] = useState({ penerimaan: 0, pembayaran: 0, transfer: 0, unclassified: 0 });
  const reloadSummary = useCallback(() => {
    fetchSummary().then(setSummary).catch(() => {});
  }, []);
  useEffect(() => { reloadSummary(); }, [reloadSummary]);

  const { canPerm } = useAccess();
  const { user } = useAuth();
  const canWrite = canPerm(user, "kas_bank_classification", "create");
  const canDelete = canPerm(user, "kas_bank_classification", "delete");
  const canManualWrite = canPerm(user, "kas_bank_manual_entry", "create");
  const canManualDelete = canPerm(user, "kas_bank_manual_entry", "delete");

  return (
    <div className="p-6 space-y-4" data-testid="kas-bank-page">
      <div>
        <div className="overline text-[10px] text-slate-500 tracking-widest">KEUANGAN</div>
        <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A] flex items-center gap-2" data-testid="kas-bank-title">
          <Landmark size={24} className="text-[#FF5A1F]" /> Kas &amp; Bank
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Kas kecil mingguan, buku bank perusahaan, dan klasifikasi mutasi.</p>
      </div>

      <div className="flex items-center gap-1 flex-wrap" data-testid="kas-bank-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" data-testid={`kb-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${tab === t.key ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "petty-cash" && <PettyCashPage embedded />}

      {tab === "buku-bank" && (
        <>
          <div className="flex items-center gap-1 flex-wrap" data-testid="buku-bank-subtabs">
            {BUKU_SUBTABS.map((s) => {
              const badge = s.key === "penerimaan" ? summary.penerimaan
                : s.key === "pembayaran" ? summary.pembayaran
                : s.key === "transfer" ? summary.transfer
                : s.key === "register" ? summary.unclassified : null;
              return (
                <button key={s.key} type="button" data-testid={`bb-sub-${s.key}`} onClick={() => setSub(s.key)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${sub === s.key ? "bg-white text-[#0B1D3A] border-[#0B1D3A]" : "bg-slate-100 text-slate-500 border-slate-200 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"}`}>
                  <s.icon size={11} /> {s.label}
                  {badge !== null && badge > 0 && (
                    <span data-testid={`bb-badge-${s.key}`} className={`inline-block px-1.5 py-0 text-[9px] font-bold tabular-nums ${s.key === "register" ? "bg-amber-100 text-amber-800" : sub === s.key ? "bg-slate-100 text-slate-700" : "bg-slate-200 text-slate-600"}`}>{badge}</span>
                  )}
                </button>
              );
            })}
          </div>
          {sub === "history-bank" && <BankHistoryTab />}
          {sub === "register" && <BankRegisterTab />}
          {sub === "penerimaan" && <ClassificationListTab kind="penerimaan" defaultKind="penerimaan" canWrite={canWrite} canDelete={canDelete} canManualWrite={canManualWrite} canManualDelete={canManualDelete} onChanged={reloadSummary} />}
          {sub === "pembayaran" && <ClassificationListTab kind="pembayaran" defaultKind="pembayaran" canWrite={canWrite} canDelete={canDelete} canManualWrite={canManualWrite} canManualDelete={canManualDelete} onChanged={reloadSummary} />}
          {sub === "transfer" && <ClassificationListTab kind="transfer" defaultKind="transfer" canWrite={canWrite} canDelete={canDelete} canManualWrite={canManualWrite} canManualDelete={canManualDelete} onChanged={reloadSummary} />}
        </>
      )}
    </div>
  );
}

/** Redirect route lama /petty-cash → /kas-bank?tab=petty-cash (query param dipertahankan). */
export function PettyCashLegacyRedirect() {
  const loc = useLocation();
  const extra = loc.search ? loc.search.replace(/^\?/, "") : "";
  return <Navigate to={`/kas-bank?tab=petty-cash${extra ? `&${extra}` : ""}`} replace />;
}
